/**
 * Rebasage des colonnes de MOIS sur l'année regardée (écart E9).
 *
 * Pourquoi ce module existe : les tableaux à 12 mois portent des conditions de colonne bornant
 * chacune un mois d'une année **figée à l'installation** — une condition compare une colonne à un
 * littéral, elle ne sait pas extraire le mois-de-l'année. Filtrer la page sur une autre année
 * donnait donc douze colonnes vides, sans le moindre message.
 *
 * ⚠️ Les marqueurs `monthOfYear` / `monthAxisFieldUUID` sont posés À PLAT sur la colonne, pas sous
 * une clé `extra` : la fabrique `col()` du back étale son `extra` au premier niveau, comme elle le
 * fait déjà pour `colorThresholds` et `displayModeValue`.
 *
 * ⚠️ Ce sont bien janvier à décembre de l'année regardée, PAS une fenêtre glissante de douze mois.
 * Le client intitule ses colonnes « Janvier … Décembre », sans année : une fenêtre glissante
 * exigerait des en-têtes datés, faute de quoi « Janvier » désignerait tantôt un mois, tantôt un
 * autre. Le rebasage garde donc les en-têtes exacts.
 */

const deux = n => String(n).padStart(2, "0")

/** L'année d'une borne `AAAA-MM-JJ` ou d'une date lisible, ou `null`. */
export function anneeDe(valeur) {
  if (valeur === null || valeur === undefined) return null
  const texte = String(valeur).trim()

  const iso = /^(\d{4})-\d{2}-\d{2}/.exec(texte)
  if (iso) return Number(iso[1])

  const fr = /^\d{1,2}\/\d{1,2}\/(\d{4})/.exec(texte)
  if (fr) return Number(fr[1])

  const n = Number(texte)
  if (Number.isFinite(n) && n > 1e9) return new Date(n > 1e12 ? n : n * 1000).getFullYear()

  return null
}

/**
 * L'année sur laquelle rebaser, d'après les filtres actifs de la page, ou `null`.
 *
 * ⚠️ On ne regarde QUE les filtres portant sur le champ qui sert d'axe aux colonnes. Un filtre de
 * date sur un autre champ ne dit rien de la période regardée, et s'en servir déplacerait les
 * colonnes sur un critère sans rapport.
 *
 * ⚠️ Quand la période couvre plusieurs années, c'est l'année de FIN qui gagne : c'est celle vers
 * laquelle on regarde. Choisir l'année de début montrerait un passé qu'on vient de quitter.
 */
export function anneeVisee(filtresDePage, axeFieldUUID) {
  if (!axeFieldUUID) return null

  let debut = null
  let fin = null

  for (const filtre of Object.values(filtresDePage ?? {})) {
    if (!filtre || filtre.fieldUUID !== axeFieldUUID) continue

    // Un filtre calendaire porte ses deux bornes dans `value`, un filtre simple une seule valeur.
    const valeurs = Array.isArray(filtre.value) ? filtre.value : [filtre.value]
    for (const v of valeurs) {
      const an = anneeDe(v)
      if (an === null) continue
      if (debut === null || an < debut) debut = an
      if (fin === null || an > fin) fin = an
    }
  }

  return fin ?? debut
}

/**
 * Réécrit les bornes d'une condition de mois sur l'année visée.
 *
 * ⚠️ La borne de fin est EXCLUE (`<`), jamais un `<=` sur le dernier jour du mois. La colonne est un
 * horodatage et le moteur convertit « 2026-01-31 » en minuit pile : un `<=` perdrait toute
 * évaluation du 31 portant une heure. Sur ce tenant, 15 valeurs portent une heure — un artefact de
 * changement d'heure — et il suffirait qu'une seule tombe un dernier jour de mois pour qu'une
 * colonne mente sans rien signaler.
 *
 * @param mois  le mois de la colonne, de 1 à 12
 * @param annee l'année visée
 * @param champ le champ de date servant d'axe
 * @param label libellé repris sur les feuilles, pour la lisibilité du filtre
 */
export function bornesDuMois(mois, annee, champ, label = "") {
  const moisSuivant = mois === 12 ? 1 : mois + 1
  const anneeSuivante = mois === 12 ? annee + 1 : annee
  const feuille = (operation, value) => ({ fieldUUID: champ, operation, value, label })
  return {
    operation: "and",
    filters: [
      { operation: "or", filters: [feuille(">=", `${annee}-${deux(mois)}-01`)] },
      { operation: "or", filters: [feuille("<", `${anneeSuivante}-${deux(moisSuivant)}-01`)] },
    ],
  }
}

/**
 * Rebase les colonnes de mois d'un widget sur l'année regardée.
 *
 * Ne touche à rien si aucune colonne ne se déclare « colonne de mois », ou si les filtres de page
 * ne désignent aucune année : le widget garde alors les bornes posées à l'installation, ce qui est
 * le comportement d'avant.
 *
 * @returns un nouveau tableau de colonnes (l'entrée n'est pas modifiée)
 */
export function rebaserColonnesDeMois(colonnes, filtresDePage) {
  const liste = Array.isArray(colonnes) ? colonnes : []
  const declarees = liste.filter(c => Number.isInteger(c?.monthOfYear))
  if (declarees.length === 0) return liste

  const axe = declarees[0]?.monthAxisFieldUUID
  const annee = anneeVisee(filtresDePage, axe)
  if (!annee) return liste

  return liste.map(colonne => {
    const mois = colonne?.monthOfYear
    if (!Number.isInteger(mois) || mois < 1 || mois > 12) return colonne
    const champ = colonne?.monthAxisFieldUUID
    if (!champ) return colonne
    const bornes = bornesDuMois(mois, annee, champ, colonne?.name ?? "")
    return { ...colonne, conditions: { ...bornes, filters: [...bornes.filters, ...autresConditions(colonne, champ)] } }
  })
}

/** Vrai si un bloc de condition (feuille ou groupe) ne porte QUE sur `champ`. */
const porteSeulementSur = (bloc, champ) =>
  Array.isArray(bloc?.filters)
    ? bloc.filters.length > 0 && bloc.filters.every(sous => porteSeulementSur(sous, champ))
    : bloc?.fieldUUID === champ

/**
 * Les blocs de la condition d'origine qui NE portent PAS sur l'axe des mois — à conserver.
 *
 * ⚠️ Le rebasage ne remplace que les BORNES de date. Une colonne peut porter d'autres conditions :
 * depuis le 13/10/2026, les douze moyennes du « Suivi des taux de conformité » ne prennent que les
 * évaluations « Répondue ». Remplacer toute la condition les perdait dès qu'un filtre d'année était
 * posé — la moyenne de mars 2027 reprenait les évaluations non répondues, sans rien signaler.
 */
function autresConditions(colonne, champ) {
  const origine = colonne?.conditions
  if (!origine || !Array.isArray(origine.filters)) return []
  if (String(origine.operation ?? "and").toLowerCase() !== "and") return []
  return origine.filters.filter(bloc => !porteSeulementSur(bloc, champ))
}
